import { createElement, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import {
  HIDE_JOBS_INVITE_POPUP,
  SHOW_MESSAGE,
} from '~/actions';
import { fireProfessionalSelected, type TProCategory } from '~/posthogs/events';

const INVITE_TAB = {
  FAVORITES: 'favorites',
  WORKED_WITH: 'worked-with',
  NEW_TO_YOU: 'new-to-you',
} as const;
type TInviteTab = typeof INVITE_TAB[keyof typeof INVITE_TAB];
import { IFavoriteProfessional, useFavoriteProfessionals } from '~/domain/FavoriteProfessionals';
import { IRecentProfessional, useRecentProfessionals } from '~/domain/RecentProfessionals';
import { useRecommendedProfessionals } from '~/domain/RecommendedProfessionals';
import {
  useGetJob,
  useInviteFavoriteToJobMutation,
  useInviteProfessionalsToJobsMutation,
} from '~/domain/Jobs';
import { jobKeys } from '~/domain/Jobs/jobKeys';
import { useIsError, useIsSuccess, useLoading } from '~/hooks';
import { IFavoriteInvite, ProfessionTypeEnum } from '~/models';

const tabToTProCategory: Record<TInviteTab, TProCategory> = {
  [INVITE_TAB.FAVORITES]: 'Favorite',
  [INVITE_TAB.WORKED_WITH]: 'Worked With',
  [INVITE_TAB.NEW_TO_YOU]: 'New to You',
};

interface IProps {
  jobId?: string;
  jobIds?: string[];
  onClosePopup: () => void;
  isRefetchJob?: boolean;
  defaultProfessionals?: string[];
  isOpen?: boolean;
  filterProfession?: ProfessionTypeEnum;
  officeId?: string;
  onInviteSuccess?: () => void;
}

/**
 * @param {boolean} isRefetchJob - Flag to keep the popup loading while job detail query loads.
 * @param {string} jobId - Job ID to send the invitation to. Ignored if `jobIds` is provided.
 * @param {string[]} jobIds - List of job IDs to send invitations to. Takes precedence over `jobId` if defined.
 * @param {function} onClosePopup - Callback function to close the popup.
 * @param {string[]} defaultProfessionals -  default professionals to be selected by default.
 * @param {boolean} isOpen - Flag to determine if the popup is open.
 * @param {ProfessionTypeEnum} filterProfession - Profession type to filter professionals.
 * @param {function} onInviteSuccess - Called when the popup closes after a successful invite, so the caller can refetch the source of truth.
 */
export const useInviteProfessional = ({
  isRefetchJob = true,
  jobId,
  jobIds,
  onClosePopup,
  defaultProfessionals,
  isOpen,
  filterProfession,
  officeId,
  onInviteSuccess,
}: IProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const inviteFavoriteToJobMutation = useInviteFavoriteToJobMutation();
  const inviteProfessionalsToJobsMutation = useInviteProfessionalsToJobsMutation();
  const { data: favoriteProfessionals, isLoading: isLoadingFavoriteProfessionals } =
    useFavoriteProfessionals({
      officeId: officeId || '',
      jobId: jobId,
      enabled: !!isOpen,
    });
  const { data: recentProfessionals, isLoading: isLoadingRecentProfessionals } =
    useRecentProfessionals({
      officeId: officeId || '',
      jobId: jobId,
      enabled: !!isOpen,
    });
  const { data: recommendedProfessionals, isLoading: isLoadingRecommendedProfessionals } =
    useRecommendedProfessionals({
      officeId: officeId || '',
      enabled: !!isOpen,
    });
  const { data: jobDetail, isLoading: isLoadingJobDetail } = useGetJob(jobId, {
    enabled: !!isOpen && !!jobId,
  });

  const isProfessionalAvailable = (isAvailable: boolean | undefined) => {
    return isAvailable ?? true;
  };

  const favoritesListFiltered = useMemo(
    () =>
      (favoriteProfessionals || []).filter((pro: IFavoriteProfessional) =>
        filterProfession
          ? pro.professions?.includes(filterProfession) && isProfessionalAvailable(pro.isAvailable)
          : isProfessionalAvailable(pro.isAvailable),
      ),
    [favoriteProfessionals, filterProfession],
  );

  const recentsListFiltered = useMemo(
    () =>
      (recentProfessionals || []).filter((pro: IRecentProfessional) =>
        filterProfession
          ? pro.professional?.professions?.includes(filterProfession) &&
            isProfessionalAvailable(pro.isAvailable)
          : isProfessionalAvailable(pro.isAvailable),
      ),
    [recentProfessionals, filterProfession],
  );

  const recommendedListFiltered = useMemo(
    () =>
      (recommendedProfessionals || []).filter((pro) =>
        filterProfession
          ? pro.professions?.includes(filterProfession) && isProfessionalAvailable(pro.isAvailable)
          : isProfessionalAvailable(pro.isAvailable),
      ),
    [recommendedProfessionals, filterProfession],
  );

  const { isLoading: isInviteStatusLoading } = useLoading(['INVITE_FAVORITE_TO_JOB']);
  const isInviting =
    isInviteStatusLoading ||
    inviteFavoriteToJobMutation.isPending ||
    inviteProfessionalsToJobsMutation.isPending;
  const isLoading =
    isLoadingFavoriteProfessionals ||
    isLoadingRecentProfessionals ||
    (isRefetchJob && isLoadingJobDetail) ||
    isLoadingRecommendedProfessionals;

  const { clearError, isError } = useIsError(['INVITE_FAVORITE_TO_JOB']);
  const { clearSuccess, isSuccess } = useIsSuccess(['INVITE_FAVORITE_TO_JOB']);

  const [activeTab, setActiveTab] = useState<TInviteTab>(INVITE_TAB.FAVORITES);
  const [selectedProfessionals, setSelectedProfessionals] = useState<Set<string>>(new Set());
  const sentProfessionalIds = useMemo(() => {
    if (!jobId || jobDetail?.id !== jobId) return new Set<string>();

    return new Set(
      (jobDetail.favoriteInvites ?? []).map(
        (it: IFavoriteInvite) => it?.professional as unknown as string,
      ),
    );
  }, [jobDetail, jobId]);

  const handleClosePopup = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation();
      // When the popup is dismissed after a successful invite, let the caller
      // refetch the source of truth (e.g. the job) so any invite-derived UI such
      // as the shift-details "X invited" count reflects the real backend state.
      if (isSuccess) {
        onInviteSuccess?.();
      }
      clearError();
      clearSuccess();
      onClosePopup();
      setActiveTab(INVITE_TAB.FAVORITES);
      setSelectedProfessionals(new Set());
    },
    [clearError, clearSuccess, onClosePopup, isSuccess, onInviteSuccess],
  );

  useEffect(() => {
    if (isOpen && defaultProfessionals) {
      setSelectedProfessionals(new Set(defaultProfessionals));
    }
  }, [isOpen, defaultProfessionals]);

  useEffect(() => {
    if (isError) {
      handleClosePopup();
    }
  }, [handleClosePopup, isError]);

  useEffect(() => {
    if (favoritesListFiltered?.length) {
      setActiveTab(INVITE_TAB.FAVORITES);
    } else if (recentsListFiltered?.length >= 3) {
      setActiveTab(INVITE_TAB.WORKED_WITH);
    } else if (recommendedListFiltered?.length) {
      setActiveTab(INVITE_TAB.NEW_TO_YOU);
    }
  }, [favoritesListFiltered, recentsListFiltered, recommendedListFiltered]);

  const onSelectProfessionals = useCallback((ids: string[], forceAdd: boolean = false) => {
    setSelectedProfessionals((prevSelected) => {
      const newSelected = new Set(prevSelected);

      ids.forEach((id) => {
        if (newSelected.has(id) && !forceAdd) {
          newSelected.delete(id);
        } else {
          if (!newSelected.has(id)) {
            fireProfessionalSelected({
              professionalId: id,
              proCategory: tabToTProCategory[activeTab] || 'Favorite',
            });
          }
          newSelected.add(id);
        }
      });

      return newSelected;
    });
  }, [activeTab]);

  const onSentInvitations = useCallback(() => {
    if (jobIds?.length) {
      inviteProfessionalsToJobsMutation.mutate(
        {
          jobIds,
          professionalIds: [...selectedProfessionals],
          officeId,
        },
        {
          onSuccess: (_data, payload) => {
            payload.jobIds.forEach((id) => {
              queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
            });

            dispatch({ type: HIDE_JOBS_INVITE_POPUP });
            setTimeout(() => {
              dispatch({
                type: SHOW_MESSAGE,
                payload: {
                  title: 'Invite(s) Successfully Sent!',
                  message: createElement(
                    'span',
                    null,
                    'Your shifts were sent to the GoTu community, and invited professionals received alerts for days they’re available.',
                    createElement('br'),
                    'Note: Professionals won’t receive invites for days in which they’re unavailable.',
                  ),
                  isSuccess: true,
                  buttonText: 'Close',
                },
              });
            }, 500);
          },
        },
      );

      return;
    }

    if (!jobId) {
      return;
    }

    inviteFavoriteToJobMutation.mutate({
      jobId,
      userIds: [...selectedProfessionals],
      officeId,
    });
  }, [
    inviteFavoriteToJobMutation,
    inviteProfessionalsToJobsMutation,
    jobId,
    jobIds,
    selectedProfessionals,
    officeId,
    dispatch,
    queryClient,
  ]);

  return {
    activeTab,
    isInviting,
    isLoading,
    isSuccess,
    selectedProfessionals,
    onClosePopup: handleClosePopup,
    onSelectProfessionals,
    onSentInvitations,
    onTabChange: setActiveTab,
    favoriteProfessionals: favoritesListFiltered,
    recentProfessionals: recentsListFiltered,
    recommendedProfessionals: recommendedListFiltered,
    sentProfessionalIds,
    jobProfession: jobDetail?.profession,
  };
};
